import { useEffect, useState } from "react";
import { fetchJson, FREE_NEURONS_PER_DAY, type AiUsage } from "../lib/api";

interface ChatResponse {
  reply: string;
  model: string;
  neuronsCharged: number;
  usage: AiUsage;
}

const FREE_MODELS = [
  "@cf/meta/llama-3.3-70b-instruct-fp8-fast",
  "@cf/qwen/qwen3-30b-a3b-fp8",
  "@cf/google/gemma-3-12b-it",
];

export function AiPage() {
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [model, setModel] = useState(FREE_MODELS[0]);
  const [prompt, setPrompt] = useState("Summarize today's trading risk posture in one paragraph.");
  const [reply, setReply] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUsage = async () => {
    const next = await fetchJson<AiUsage>("/ai/usage");
    setUsage(next);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadUsage();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const percent = usage?.percentUsed ?? 0;
  const meterClass = usage?.atLimit ? "danger" : usage?.nearLimit ? "warn" : "ok";

  const chat = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<ChatResponse>("/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model, prompt }),
      });
      setReply(body.reply);
      setUsage(body.usage);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      try {
        await loadUsage();
      } catch {
        // ignore secondary failure
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-pad">
      <div className="page-header">
        <div>
          <h1>Workers AI</h1>
          <p>
            Cloudflare Workers AI with accurate free-tier metering: {FREE_NEURONS_PER_DAY.toLocaleString()}{" "}
            neurons per day, reset at 00:00 UTC.
          </p>
        </div>
        <span className={`badge ${meterClass}`}>
          {usage ? `${usage.remainingNeurons.toLocaleString()} left` : "loading"}
        </span>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="panel stack">
        <h2>Daily free allotment</h2>
        <div className="meter" aria-label="Neuron usage">
          <span style={{ width: `${Math.min(100, percent)}%` }} />
        </div>
        <div className="row">
          <span className="muted">
            Used {usage?.usedNeurons.toLocaleString() ?? "—"} /{" "}
            {usage?.freeAllotment.toLocaleString() ?? FREE_NEURONS_PER_DAY.toLocaleString()} neurons
          </span>
          <span className="muted">
            Reset {usage ? new Date(usage.resetAtUtc).toUTCString() : "00:00 UTC"}
          </span>
          <span className="muted">Source: {usage?.source ?? "—"}</span>
        </div>
        {usage?.nearLimit && !usage.atLimit ? (
          <p className="badge warn">Approaching free limit (≥80%).</p>
        ) : null}
        {usage?.atLimit ? (
          <p className="badge danger">Free daily allocation exhausted. New AI calls are blocked.</p>
        ) : null}
      </div>

      <div className="panel stack" style={{ marginTop: "1rem" }}>
        <h2>Chat</h2>
        <label className="field">
          Model (free-plan safe)
          <select value={model} onChange={(e) => setModel(e.target.value)}>
            {FREE_MODELS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Prompt
          <textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </label>
        <div className="row">
          <button
            className="btn primary"
            type="button"
            disabled={busy || usage?.atLimit}
            onClick={() => void chat()}
          >
            {busy ? "Running…" : "Run model"}
          </button>
          <button className="btn" type="button" onClick={() => void loadUsage()}>
            Refresh usage
          </button>
        </div>
        {reply ? (
          <div className="feed-item">
            <div className="meta">model reply</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{reply}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
